import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NauticusLogo } from "@/components/NauticusLogo";
import { SBALogo } from "@/components/SBALogo";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, LibraryBig, ClipboardCheck, UserCog, LogOut,
  Building2, Briefcase, SlidersHorizontal, Menu, X, Route, Stethoscope, History, MessageSquare, Database, Pill, Brain,
} from "lucide-react";

const HelmMark = ({ className = "" }) => (
  <img src="/brand/helm_wheel.png" alt="" className={`object-contain ${className}`} data-testid="helm-mark" />
);

const NAV = {
  employee: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/household", label: "My Household", icon: Users },
    { to: "/app/benefits", label: "Benefits", icon: LibraryBig },
    { to: "/app/enrollments", label: "My Coverage", icon: ClipboardCheck },
    { to: "/app/medicare", label: "Medicare", icon: Stethoscope },
    { to: "/app/profile", label: "Profile", icon: UserCog },
  ],
  employer_admin: [
    { to: "/employer", label: "Overview", icon: Building2, end: true },
  ],
  advisor: [
    { to: "/advisor", label: "Client Book", icon: Briefcase, end: true },
    { to: "/advisor/medicare", label: "Medicare Journey", icon: Stethoscope },
    { to: "/advisor/handoffs", label: "Hand-offs", icon: History },
    { to: "/agent", label: "Helm", icon: HelmMark },
  ],
  platform_admin: [
    { to: "/admin", label: "Platform", icon: SlidersHorizontal, end: true },
    { to: "/admin/enrollment", label: "Enrollment Router", icon: Route },
    { to: "/agent", label: "Helm", icon: HelmMark },
    { to: "/admin/ask-qa", label: "Ask Nauticus QA", icon: MessageSquare },
    { to: "/admin/ask-orchestrator", label: "Ask Orchestrator QA", icon: Brain },
    { to: "/admin/plan-intel", label: "Plan Intelligence QA", icon: Database },
    { to: "/admin/part-d-intel", label: "Part D Formulary QA", icon: Pill },
    { to: "/admin/provider-intel", label: "Provider Network QA", icon: Stethoscope },
  ],
};

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isConsumer = user?.role === "employee" && !user?.employer_id;
  const CONSUMER_NAV = [
    { to: "/app/member", label: "Home", icon: LayoutDashboard, end: true },
    { to: "/app/ask", label: "Ask Nauticus", icon: MessageSquare },
    { to: "/app/household", label: "My Nauticus", icon: Users },
    { to: "/app/profile", label: "Profile", icon: UserCog },
  ];
  const items = isConsumer ? CONSUMER_NAV : (NAV[user?.role] || []);
  const Brand = user?.consumer_brand === "sba" ? SBALogo : NauticusLogo;

  const isActive = (item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "U")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("");

  return (
    <div className="min-h-screen bg-mist-100 flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-navy-800 flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="app-sidebar"
      >
        <div className="px-6 py-6 border-b border-white/10 flex items-center justify-between">
          <Brand light />
          <button className="lg:hidden text-white/70" onClick={() => setOpen(false)} data-testid="close-sidebar">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1">
          {items.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                  active
                    ? "bg-gold text-navy-900"
                    : "text-mist-300 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className="w-[18px] h-[18px]" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-4 py-5 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="w-9 h-9 rounded-full bg-gold text-navy-900 flex items-center justify-center font-bold text-sm">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-mist-300 text-xs capitalize truncate">{isConsumer ? "Member" : user?.role?.replace("_", " ")}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start text-mist-300 hover:text-white hover:bg-white/5"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-20 bg-navy-800 px-4 py-3 flex items-center justify-between">
          <Brand light />
          <button onClick={() => setOpen(true)} className="text-white" data-testid="open-sidebar">
            <Menu className="w-6 h-6" />
          </button>
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
